import { Dispatch, SetStateAction, useState } from 'react';

import { Box, Button, TextField, Typography, Divider } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import logo from '@src/assets/logo.svg';
import { colors } from '@src/styles';
import { getEnvironmentLabel } from '@src/constants/environment';

export type TLoginView = {
  error: boolean;
  handleSubmit: () => void;
  isLoading: boolean;
  setEmail: Dispatch<SetStateAction<string>>;
  setPassword: Dispatch<SetStateAction<string>>;
  isReady: boolean;
  handleSSO?: () => void;
};

const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";

const monoLabelSX = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '10px',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
} as const;

const MOTIFS = [
  { left: '14%', top: '18%', size: 10, round: true, color: colors.primary.yellow, opacity: 0.85 },
  { left: '22%', top: '34%', size: 7, round: true, color: colors.brand.textStrong, opacity: 0.45 },
  { left: '38%', top: '16%', size: 12, round: false, color: colors.primary.yellow, opacity: 0.6 },
  { left: '62%', top: '42%', size: 9, round: true, color: colors.primary.blue, opacity: 0.7 },
  { left: '28%', top: '66%', size: 11, round: false, color: colors.primary.yellow, opacity: 0.45 },
  { left: '43%', top: '78%', size: 8, round: true, color: colors.primary.yellow, opacity: 0.55 },
  { left: '72%', top: '73%', size: 6, round: true, color: colors.brand.textStrong, opacity: 0.3 },
];

const lightInputSX = {
  marginBottom: 2,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    color: colors.brand.ink,
    borderRadius: '8px',
    '& fieldset': { borderColor: 'rgba(10, 13, 15, 0.18)' },
    '&:hover fieldset': { borderColor: colors.brand.indigo },
    '&.Mui-focused fieldset': { borderColor: colors.brand.indigo },
  },
  '& .MuiInputLabel-root': {
    color: '#4B5563',
    '&.Mui-focused': { color: colors.brand.indigo },
  },
} as const;

export const LoginView = ({ error, handleSubmit, setEmail, setPassword, isReady, handleSSO }: TLoginView) => {
  const [showCredentials, setShowCredentials] = useState(false);
  const environment = getEnvironmentLabel();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexWrap: 'wrap',
        backgroundColor: colors.background.primary,
        fontFamily: titleFontFamily,
      }}
    >
      <Box
        sx={{
          flex: '999 1 620px',
          minWidth: 0,
          minHeight: 620,
          position: 'relative',
          overflow: 'hidden',
          borderRight: `1px solid ${colors.brand.line}`,
          padding: { xs: '40px 28px', md: '48px 56px' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {MOTIFS.map((motif) => (
          <Box
            key={`${motif.left}-${motif.top}`}
            aria-hidden
            sx={{
              position: 'absolute',
              left: motif.left,
              top: motif.top,
              width: motif.size,
              height: motif.size,
              opacity: motif.opacity,
              borderRadius: motif.round ? '999px' : 0,
              backgroundColor: motif.round ? motif.color : 'transparent',
              border: motif.round ? 'none' : `1px solid ${motif.color}`,
            }}
          />
        ))}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            right: -180,
            bottom: -200,
            width: 520,
            height: 520,
            borderRadius: '999px',
            border: '1px solid rgba(255, 160, 21, 0.12)',
          }}
        />

        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              border: '1px solid rgba(255, 160, 21, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box component="img" src={logo} alt="Tarken" sx={{ width: 24, height: 25 }} />
          </Box>
          <Typography sx={{ fontSize: '19px', fontWeight: 600, letterSpacing: '-0.01em', color: colors.primary.white }}>
            Tarken Solutions
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', marginTop: 'auto', maxWidth: 560 }}>
          <Typography
            component="h1"
            sx={{
              margin: '0 0 20px',
              fontSize: { xs: '36px', md: '52px' },
              fontWeight: 500,
              letterSpacing: '-0.04em',
              lineHeight: 1.04,
              color: colors.primary.white,
            }}
          >
            O painel interno de quem faz a operação{' '}
            <Box component="span" sx={{ fontStyle: 'italic', color: colors.primary.yellow }}>
              rodar.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: '16px', lineHeight: 1.6, color: colors.brand.textSecondary }}>
            Automações, tasks, recursos canônicos, scripts e o histórico de execuções de todas as organizações — em um
            só lugar, para o time de Solutions.
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'relative',
            marginTop: 6,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3.5,
            ...monoLabelSX,
            color: colors.brand.textMuted,
          }}
        >
          {['Automations', 'Tasks', 'Canonicals', 'Scripts', 'Runs'].map((label) => (
            <Typography key={label} sx={{ ...monoLabelSX, color: colors.brand.textMuted }}>
              {label}
            </Typography>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          flex: '1 1 460px',
          minWidth: 0,
          backgroundColor: colors.brand.cream,
          color: colors.brand.ink,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: { xs: '40px 24px', md: '56px 48px' },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380, margin: '0 auto' }}>
          <Typography sx={{ ...monoLabelSX, color: '#6B7280' }}>
            {environment === 'PROD'
              ? 'Ambiente de produção'
              : environment === 'HML'
                ? 'Ambiente de homologação'
                : 'Ambiente local'}
          </Typography>
          <Typography
            component="h2"
            sx={{
              margin: '14px 0 10px',
              fontSize: '32px',
              fontWeight: 500,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}
          >
            Bem-vindo de volta
          </Typography>
          <Typography sx={{ margin: '0 0 28px', fontSize: '15px', lineHeight: 1.55, color: '#4B5563' }}>
            Entre com sua conta corporativa Tarken para acessar os serviços internos.
          </Typography>

          <Button
            fullWidth
            onClick={handleSSO}
            startIcon={<ShieldOutlinedIcon sx={{ color: colors.primary.yellow }} />}
            sx={{
              minHeight: 52,
              borderRadius: '8px',
              backgroundColor: colors.brand.ink,
              color: colors.brand.cream,
              fontFamily: titleFontFamily,
              fontSize: '15px',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#15191C' },
            }}
          >
            Entrar com SSO
          </Button>

          <Typography sx={{ ...monoLabelSX, marginTop: 2, textAlign: 'center', color: '#6B7280' }}>
            Autenticação via AWS Cognito · SAML
          </Typography>

          {error && (
            <Typography sx={{ marginTop: 2, textAlign: 'center', fontSize: '13.5px', color: '#B42318' }}>
              Não foi possível entrar. Verifique as credenciais ou tente novamente pelo SSO.
            </Typography>
          )}

          <Divider sx={{ marginY: 3.5, borderColor: 'rgba(10, 13, 15, 0.12)' }}>
            <Typography sx={{ ...monoLabelSX, color: '#6B7280' }}>ou</Typography>
          </Divider>

          {showCredentials ? (
            <Box>
              <TextField
                label="Email"
                type="email"
                fullWidth
                error={error}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSubmit();
                }}
                sx={lightInputSX}
              />
              <TextField
                label="Senha"
                type="password"
                fullWidth
                error={error}
                onChange={(event) => setPassword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSubmit();
                }}
                sx={lightInputSX}
              />
              <Button
                fullWidth
                disabled={!isReady}
                onClick={handleSubmit}
                sx={{
                  minHeight: 48,
                  borderRadius: '8px',
                  border: `1px solid ${colors.brand.indigo}`,
                  color: colors.brand.indigo,
                  fontFamily: titleFontFamily,
                  fontSize: '14px',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(41, 61, 147, 0.08)' },
                  '&:disabled': { borderColor: 'rgba(10, 13, 15, 0.18)', color: '#9CA3AF' },
                }}
              >
                Entrar com e-mail e senha
              </Button>
            </Box>
          ) : (
            <Button
              fullWidth
              onClick={() => setShowCredentials(true)}
              sx={{
                minHeight: 48,
                borderRadius: '8px',
                color: colors.brand.indigo,
                fontFamily: titleFontFamily,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'rgba(41, 61, 147, 0.08)' },
              }}
            >
              Entrar com e-mail e senha
            </Button>
          )}

          <Box sx={{ marginTop: 4.5, paddingTop: 3, borderTop: '1px solid rgba(10, 13, 15, 0.12)' }}>
            <Typography sx={{ ...monoLabelSX, color: '#6B7280' }}>Sem acesso?</Typography>
            <Typography sx={{ marginTop: 1.25, fontSize: '13.5px', lineHeight: 1.55, color: '#4B5563' }}>
              O acesso é liberado pelo time de Plataforma para contas <strong>@tarken.ag</strong> com papel de staff.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
